import { CalendarClock, History, MoreHorizontal, Pencil, ShieldOff, Undo2, UserCog } from "lucide-react";

import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaDropdownMenu, type ColocaDropdownItem } from "@/shared/components/overlays/ColocaDropdownMenu";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { compareDecimalStrings, trimTrailingZeros } from "@/shared/utils/decimal";

import { countryLabel, currencyOfCountry } from "../../constants/paymentTaxes.constants";
import type { PaymentTax, TaxScopeGroup, TaxSummary } from "../../types/paymentTaxes.types";
import { effectiveOwnScope, exceptionsOf } from "../../utils/taxMath";
import { CountryFlag } from "../fees/CorridorIdentity";
import { TaxExample } from "./TaxExample";
import { sameScope, scopeLabel, whereLabel } from "./taxScopeLabels";

export interface TaxRowActions {
  onChange: (summary: TaxSummary, group: TaxScopeGroup) => void;
  onHistory: (summary: TaxSummary, group: TaxScopeGroup, inherited: boolean) => void;
  onSpecial: (summary: TaxSummary, general: TaxScopeGroup) => void;
  onExempt: (summary: TaxSummary, general: TaxScopeGroup) => void;
  onBackToGeneral: (summary: TaxSummary, own: TaxScopeGroup) => void;
}

export interface TaxCountrySectionProps {
  country: string;
  taxes: TaxSummary[];
  /** Null: the general taxes; otherwise what this organization pays. */
  scopeOrgId: string | null;
  now: number;
  canWrite: boolean;
  actions: TaxRowActions;
}

const isExempt = (tax: PaymentTax | null) => !!tax && compareDecimalStrings(tax.rate, "0") === 0;
const hasScheduled = (group: TaxScopeGroup, now: number) =>
  group.versions.some((version) => new Date(version.valid_from).getTime() > now);

export function TaxCountrySection({
  country,
  taxes,
  scopeOrgId,
  now,
  canWrite,
  actions,
}: TaxCountrySectionProps) {
  const { t, activeLanguage } = useTranslations("paymentTaxes");
  const currency = currencyOfCountry(country);

  return (
    <section className="space-y-2" aria-labelledby={`taxes-${country}`}>
      <h2 id={`taxes-${country}`} className="flex items-center gap-2 text-sm font-semibold">
        <CountryFlag iso3={country} />
        {countryLabel(country, activeLanguage)}
        <span className="font-mono text-xs font-normal text-muted-foreground">{currency}</span>
      </h2>
      <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
        {taxes
          .flatMap((summary) => rowsOf(summary, scopeOrgId))
          .map(({ summary, general, own }) => {
            const shown = own ?? general;
            const inherited = !!own && !sameScope(own, general);
            const tax = shown.current ?? shown.versions[0] ?? null;
            const exceptions = scopeOrgId ? 0 : exceptionsOf(summary, general);
            const menu: ColocaDropdownItem[] = [];
            if (canWrite) {
              if (!scopeOrgId) {
                menu.push({
                  label: t("action_change"),
                  icon: Pencil,
                  onClick: () => actions.onChange(summary, general),
                });
              } else if (inherited) {
                menu.push({
                  label: t("action_special_here"),
                  icon: UserCog,
                  onClick: () => actions.onSpecial(summary, general),
                });
              } else if (own) {
                menu.push(
                  {
                    label: t("action_change_special"),
                    icon: Pencil,
                    onClick: () => actions.onChange(summary, own),
                  },
                  {
                    label: t("action_back_general"),
                    icon: Undo2,
                    onClick: () => actions.onBackToGeneral(summary, own),
                  },
                );
              } else {
                menu.push(
                  {
                    label: t("action_exempt"),
                    icon: ShieldOff,
                    onClick: () => actions.onExempt(summary, general),
                  },
                  {
                    label: t("action_special"),
                    icon: UserCog,
                    onClick: () => actions.onSpecial(summary, general),
                  },
                );
              }
            }
            menu.push({
              label: t("action_history"),
              icon: History,
              onClick: () => actions.onHistory(summary, shown, inherited),
            });

            return (
              <li
                key={`${general.key}>${shown.key}`}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3"
              >
                <div className="min-w-48 flex-1">
                  <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    {summary.name}
                    <ColocaBadge variant="secondary" className="font-mono">
                      {summary.code}
                    </ColocaBadge>
                    {scopeOrgId && (
                      <ColocaBadge variant={own ? "primary" : "default"}>
                        {t(own ? "source_own" : "source_general")}
                      </ColocaBadge>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {t(summary.base === "COMMISSION" ? "base_commission" : "base_gross")} ·{" "}
                    {whereLabel(general, t)}
                  </p>
                  {inherited && own && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t("inherited_from", { where: scopeLabel(own, t) })}
                    </p>
                  )}
                </div>

                <div className="flex min-w-40 flex-col items-start gap-1">
                  <span
                    className={cn(
                      "font-mono text-lg font-semibold tabular-nums",
                      isExempt(tax) ? "text-muted-foreground" : "text-foreground",
                    )}
                  >
                    {!tax ? "—" : isExempt(tax) ? t("exempt") : `${trimTrailingZeros(tax.rate)} %`}
                  </span>
                  {tax && !isExempt(tax) && (
                    <TaxExample base={summary.base} name={summary.name} rate={tax.rate} currency={currency} />
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {tax && !isExempt(tax) && (
                    <ColocaBadge variant={tax.bearer === "CLIENT" ? "default" : "warning"}>
                      {t(tax.bearer === "CLIENT" ? "bearer_client" : "bearer_coloca")}
                    </ColocaBadge>
                  )}
                  {!scopeOrgId && exceptions > 0 && (
                    <ColocaBadge variant="ghost">{t("exceptions_count", { count: exceptions })}</ColocaBadge>
                  )}
                  {hasScheduled(shown, now) && (
                    <ColocaBadge variant="ghost" icon={CalendarClock}>
                      {t("scheduled")}
                    </ColocaBadge>
                  )}
                  {tax && (
                    <span className="text-xs text-muted-foreground">
                      {t("since", { date: formatDateTime(tax.valid_from, { locale: activeLanguage }) })}
                    </span>
                  )}
                </div>

                <ColocaDropdownMenu
                  align="end"
                  items={menu}
                  trigger={
                    <ColocaIconButton
                      icon={MoreHorizontal}
                      variant="ghost"
                      size="sm"
                      className="ml-auto"
                      aria-label={t("actions_aria", { name: summary.name })}
                    />
                  }
                />
              </li>
            );
          })}
      </ul>
    </section>
  );
}

interface TaxRow {
  summary: TaxSummary;
  general: TaxScopeGroup;
  own: TaxScopeGroup | null;
}

function rowsOf(summary: TaxSummary, scopeOrgId: string | null): TaxRow[] {
  const own = scopeOrgId
    ? summary.organizationScopes.filter((group) => group.organizationId === scopeOrgId)
    : [];
  const rows = summary.scopes.map((general) => ({
    summary,
    general,
    own: effectiveOwnScope(general, own),
  }));
  // A shared own scope also gets its own row: that is where it is changed or removed.
  const shown = new Set(
    rows.filter((row) => row.own && sameScope(row.own, row.general)).map((row) => row.own?.key),
  );
  return [
    ...rows,
    ...own.filter((group) => !shown.has(group.key)).map((group) => ({ summary, general: group, own: group })),
  ];
}
